import { useQueries } from '@tanstack/react-query';
import { apiFetch } from '../../auth/apiFetch.ts';
import type { TelemetryReading } from '../../hooks/useTelemetry.ts';

/* ==========================================================================
   Telemetry for the robots a surface is COMPARING against, as opposed to the
   one it is about.

   Deliberately a different query key from `useTelemetry`'s. The shell resolves
   the surface's object scope — the robot named in the breadcrumb, and the
   reading count and window in the status bar — by finding the most recently
   updated mounted `['telemetry', …]` query. Fetching four robots under that
   key would leave the crumb flipping to whichever poll landed last, so the
   subject keeps the shared key and the comparands get their own.

   Same endpoint, same shape, same 5s poll: nothing about the data is
   different, only which query the shell is entitled to read the page's
   identity from.
   ========================================================================== */

async function fetchTelemetry(robotId: string, limit: number): Promise<TelemetryReading[]> {
  const r = await apiFetch(
    `/api/robots/${encodeURIComponent(robotId)}/telemetry?limit=${limit}`,
  );
  if (!r.ok) throw new Error(`status ${r.status}`);
  const body = (await r.json()) as { readings?: TelemetryReading[] };
  return body.readings ?? [];
}

export interface CompareResult {
  robotId: string;
  readings: readonly TelemetryReading[];
  loading: boolean;
}

export function useCompareTelemetry(robotIds: readonly string[], limit = 600): CompareResult[] {
  return useQueries({
    queries: robotIds.map((id) => ({
      queryKey: ['telemetry-compare', id, limit] as const,
      queryFn: () => fetchTelemetry(id, limit),
      refetchInterval: 5_000,
    })),
    combine: (results) =>
      results.map((r, i) => ({
        robotId: robotIds[i] as string,
        readings: r.data ?? [],
        loading: r.isPending,
      })),
  });
}
